import clsx from 'clsx';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { AsyncComponent, checkFeatureFlagServerAction } from '@headless-workspace/domain/common/server';
import { PropsWithLocale } from '@headless-workspace/glow-ds';
import {
    HomeBeautyTipsSection,
    HomeBrandoramaSection,
    homeDisplayStyle,
    HomeFastAccessSection,
    HomeMainBannerSection,
    HomeOpenBarServicesSection,
    HomeProductCarouselSection,
    HomeSlotType,
    HomeUnderBanners,
    HomeTargetedProductCarouselSection,
} from '../../../../src';
import { getMemoHomeSlots } from './getMemoHomeSlots';

const homeSlotClassName = 'py-1 desktop:py-2';

type HomeContentProps = PropsWithLocale;

export const HomeTemplate: AsyncComponent<HomeContentProps> = async ({ locale }) => {
    const [homeSlotsResult, openBarServicesEnabled] = await Promise.all([
        getMemoHomeSlots(locale),
        checkFeatureFlagServerAction('isOpenBarServicesEnabled', locale),
    ]);

    if (!homeSlotsResult) {
        return null;
    }

    const homeSlots = unwrapResultSuccess(homeSlotsResult);

    return homeSlots?.map((slot) => {
        switch (slot.type) {
            case HomeSlotType.FastAccess:
                return (
                    <HomeFastAccessSection
                        className={homeDisplayStyle({ displayDevice: slot.display })}
                        key={slot.id}
                        fastAccess={slot}
                    />
                );
            case HomeSlotType.MainBanner:
                return (
                    <HomeMainBannerSection
                        className={clsx('pb-1 desktop:pb-2', homeDisplayStyle({ displayDevice: slot.display }))}
                        key={slot.id}
                        banner={slot}
                    />
                );
            case HomeSlotType.UnderBanners:
                return (
                    <HomeUnderBanners
                        className={clsx(homeSlotClassName, homeDisplayStyle({ displayDevice: slot.display }))}
                        key={slot.id}
                        underBanners={slot}
                    />
                );
            case HomeSlotType.ProductRecommendations:
                return (
                    <HomeProductCarouselSection
                        key={slot.id}
                        className={clsx(homeDisplayStyle({ displayDevice: slot.display }), homeSlotClassName)}
                        title={slot.title}
                        cta={slot.cta}
                        products={slot.products}
                        locale={locale}
                        sectionType={slot.type}
                    />
                );
            case HomeSlotType.TargetedCarousel:
                return (
                    <HomeTargetedProductCarouselSection
                        homeSlotCarouselIds={slot.homeSlotCarouselIds}
                        key={slot.id}
                        carouselId={slot.id}
                        className={clsx(homeDisplayStyle({ displayDevice: slot.display }), homeSlotClassName)}
                        locale={locale}
                        sectionType={slot.type}
                    />
                );
            case HomeSlotType.Brandorama:
                return (
                    <HomeBrandoramaSection
                        className={clsx(homeSlotClassName, homeDisplayStyle({ displayDevice: slot.display }))}
                        key={slot.id}
                        brandorama={slot}
                        locale={locale}
                    />
                );
            case HomeSlotType.OpenBarServices:
                return openBarServicesEnabled ? (
                    <HomeOpenBarServicesSection
                        key={slot.id}
                        className={clsx(homeDisplayStyle({ displayDevice: slot.display }), homeSlotClassName)}
                        title={slot.title}
                        services={slot.services}
                        sectionType={slot.type}
                    />
                ) : null;
            case HomeSlotType.BeautyTips:
                return (
                    <HomeBeautyTipsSection
                        key={slot.id}
                        className={clsx(homeDisplayStyle({ displayDevice: slot.display }), homeSlotClassName)}
                        beautyTips={slot}
                        locale={locale}
                    />
                );
            default:
                return null;
        }
    });
};
